import {
    useMemo,
    useState,
} from 'react'
import pick from 'lodash/pick'

import SkyNetStepper from 'app/shared-components/SkyNetStepper'
import HistoryBlockPrompt from 'app/shared-components/HistoryBlockPrompt'
import useBeforeUnloadDialog from 'app/hooks/useBeforeUnloadDialog'
import useOrderManagementRoutes from 'app/SkyNetRoutes/hooks/useOrderManagementRoutes'

import {
    LongTermLeaseType,
} from '../longTermLease.types'
import useLongTermLeaseCreate from '../hooks/useCreateLongTermLease'
import useUpdateLongTermLease from '../hooks/useUpdateLongTermLease'
import CustomerDetails from './steps/CustomerDetails'
import {
    LongTermLeaseFormContext,
} from './LongTermLeaseFormContext'
import BookingDetails from './steps/BookingDetails'
import ContainerDetails from './steps/ContainerDetails'

const defaultProps: {
    data: LongTermLeaseType,
} = {
    data: {} as LongTermLeaseType,
}

const getSteps = ({
    activeStep,
    action,
    isPending,
    isExist,
}: {
    activeStep: number,
    action: (data: LongTermLeaseType) => void,
    isPending: boolean,
    isExist: boolean
}) => {
    return [
        {
            key: '1',
            label: 'Customer Details',
            expanded: true,
            renderContent: ({
                index,
            }: {
                index: number,
            }) => {
                return (
                    <CustomerDetails
                        disabled={activeStep !== 0}
                        index={index}
                    />
                )
            },
        },
        {
            key: '2',
            label: 'Booking Details',
            expanded: (step) => {
                return step > 0
            },
            renderContent: ({
                index,
            }: {
                index: number,
            }) => {
                return (
                    <BookingDetails
                        disabled={activeStep !== 1}
                        index={index}
                    />
                )
            },
        },
        {
            key: '3',
            label: 'Container Details',
            expanded: (step) => {
                return step > 1
            },
            content: <ContainerDetails
                action={action}
                isPending={isPending}
                disabled={isExist}
            />,
        },
    ]
}

const LongTermLeaseForm = ({
    data,
}:{data?: Readonly<LongTermLeaseType>}) => {
    const initialValue = useMemo(() => {
        return {
            firstStep: pick(data, [
                'longTermLeaseNumber',
                'customerExecution',
                'customerExecutionContacts',
                'pricingAccount',
                'customerComment',
            ]),
            secondStep: pick(data, [
                'customerReference',
                'receivedOn',
                'enteredOn',
                'tempRange',
                'incoTerm',
                'incoTermLocation',
                'leaseStartTimestamp',
                'leaseEndTimestamp',
                'commentForBillingOps',
            ]),
            thirdStep: {
                requiredContainers: data?.requiredContainers,
            },
        }
    }, [data])

    const [
        value,
        setValue,
    ] = useState<{
        firstStep: Partial<LongTermLeaseType>,
        secondStep: Partial<LongTermLeaseType>,
        thirdStep: Partial<LongTermLeaseType>,
    }>(initialValue)
    const [
        activeStep,
        setActiveStep,
    ] = useState<number>(0)

    const [
        isEdited,
        setIsEdited,
    ] = useState<boolean>()

    useBeforeUnloadDialog(isEdited)

    const {
        LongTermLease: LongTermLeaseRoutes,
    } = useOrderManagementRoutes()

    const onSuccess = ({
        id,
    }: {
        id: number,
    }) => {
        LongTermLeaseRoutes.Overview.navigate({
            params: {
                key: id || data?.id,
            },
        })
    }

    const {
        mutate: createLongTermLease,
        isPending: isPendingCreate,
    } = useLongTermLeaseCreate(onSuccess)
    const {
        mutate: updateLongTermLease,
        isPending: isPendingUpdate,
    } = useUpdateLongTermLease(data?.id, onSuccess)

    const stepperSteps = useMemo(() => {
        return getSteps({
            activeStep,
            action: data?.id ? updateLongTermLease : createLongTermLease,
            isPending: data?.id ? isPendingUpdate : isPendingCreate,
            isExist: Boolean(data?.id),
        })
    }, [
        activeStep,
        createLongTermLease,
        data?.id,
        isPendingCreate,
        isPendingUpdate,
        updateLongTermLease,
    ])

    const longTermLeaseFormContext = useMemo(() => {
        return {
            activeStep,
            setActiveStep,
            value,
            setValue,
            setIsEdited,
            initialValue,
            exists: Boolean(data.id),
        }
    }, [
        activeStep,
        value,
        initialValue,
        data.id,
    ])

    return (
        <LongTermLeaseFormContext.Provider value={longTermLeaseFormContext}>
            <SkyNetStepper
                activeStep={activeStep}
                steps={stepperSteps}
            />
            <HistoryBlockPrompt
                when={isEdited}
                positiveLabel="Discard"
                isPositiveAlert
                promptText="You are about to leave this page, your progress will be lost. Would you like to discard your changes anyway?"
            />
        </LongTermLeaseFormContext.Provider>
    )
}

LongTermLeaseForm.defaultProps = defaultProps

export default LongTermLeaseForm
